import {
  formatPlainDate,
  formatPlainTime,
  PlainDate,
  type PlainDateTimeString,
} from "@sa-nu/shared-utils/date";
import { assertNever } from "@sa-nu/shared-utils/misc";
import { formatCurrency } from "@sa-nu/shared-utils/price";

import type { FacilityUnitType } from "../../../../__generated__/gql/graphql";
import { buildBaseFee, type BaseFee } from "../_utils/facility-fee";

export type LabeledContent =
  | { label: string; format: "text"; content: string }
  | { label: string; format: "currency"; content: number; unit?: string }
  | { label: string; format: "date"; content: PlainDateTimeString }
  | { label: string; format: "datetime"; content: PlainDateTimeString }
  | {
      label: string;
      format: "datetimeRange";
      content: { start: PlainDateTimeString; end: PlainDateTimeString };
    };

type Props = {
  contents: LabeledContent[];
};

export function FacilityLabeledContents({ contents }: Props) {
  return (
    <dl className="flex flex-col space-y-[12px]">
      {contents.map((content) => (
        <div
          key={content.label}
          className="font-notojp text-skogecha flex items-start justify-between gap-x-[16px]"
        >
          <dt className="shrink-0 text-[12px] leading-[1.8] font-bold tracking-[0.24px] whitespace-nowrap md:text-[14px]">
            {content.label}
          </dt>
          <dd className="text-end text-[12px] leading-[1.8] font-medium tracking-[0.24px] whitespace-pre-line md:text-[14px]">
            {renderValue(content)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function renderValue(value: LabeledContent): React.ReactNode {
  const formatDate = (dateTime: PlainDateTimeString) =>
    formatPlainDate(PlainDate.from(dateTime), { format: "slash" });

  switch (value.format) {
    case "text":
      return <span className="whitespace-pre-line">{value.content}</span>;
    case "currency": {
      const formatted = formatCurrency(value.content, { unitCharacter: "円" });
      return value.unit ? `${formatted} (${value.unit})` : formatted;
    }
    case "date":
      return formatDate(value.content);
    case "datetime":
      return `${formatDate(value.content)} ${formatPlainTime(value.content)}`;
    case "datetimeRange":
      return `${formatPlainTime(value.content.start)}〜${formatPlainTime(value.content.end)}`;
    default:
      assertNever(value);
  }
}

// ============================================================================
// Build Functions
// ============================================================================

export function buildFacilityDetailContents(config: {
  isReserveEnabled: boolean;
  unitType: FacilityUnitType;
  reserveDeadlineHour: number;
  reserveDeadlineDaysBefore: number;
  resources: ReadonlyArray<{
    baseFeePolicies: ReadonlyArray<{ unit: number; unitFeeAmount: number }>;
  }>;
}): LabeledContent[] {
  if (!config.isReserveEnabled) {
    return [
      { label: "費用", format: "text", content: "無料" },
      { label: "利用方法", format: "text", content: "予約不要" },
    ];
  }

  const baseFee = buildBaseFee(config.resources);
  const feeContents: LabeledContent[] = baseFee
    ? [
        {
          label: "費用",
          format: "text",
          content: formatBaseFee(baseFee, config.unitType),
        },
      ]
    : [];
  return [
    ...feeContents,
    {
      label: "利用方法",
      format: "text",
      content: formatBookingMethod({
        reserveDeadlineHour: config.reserveDeadlineHour,
        reserveDeadlineDaysBefore: config.reserveDeadlineDaysBefore,
      }),
    },
  ];
}

export function buildFacilityCardContents(
  reservation?: {
    startAt: PlainDateTimeString;
    endAt: PlainDateTimeString;
    units: number | null;
    unitType: FacilityUnitType;
    chargedFeeAmount: number;
    customFieldValues?: ReadonlyArray<{ key: string; value: string | null }>;
  },
  customFields?: ReadonlyArray<{ key: string; label: string }> | null,
): LabeledContent[] {
  if (!reservation) {
    return [
      {
        label: "利用開始日",
        format: "text",
        content: "チェックイン日から",
      },
      {
        label: "利用終了日",
        format: "text",
        content: "チェックアウト日まで",
      },
      { label: "費用", format: "text", content: "無料" },
      { label: "利用方法", format: "text", content: "予約不要" },
    ];
  }

  const unitType = reservation.unitType;

  let contents: LabeledContent[];
  switch (unitType) {
    case "Slot":
      contents = [
        { label: "利用日", format: "date", content: reservation.startAt },
        {
          label: "利用時間",
          format: "datetimeRange",
          content: { start: reservation.startAt, end: reservation.endAt },
        },
      ];
      break;
    case "Night":
    case "Reservation":
      contents = [
        {
          label: "利用開始",
          format: "datetime",
          content: reservation.startAt,
        },
        { label: "利用終了", format: "datetime", content: reservation.endAt },
      ];
      break;
    default:
      assertNever(unitType);
  }

  for (const field of customFields ?? []) {
    const entry = reservation.customFieldValues?.find(
      (e) => e.key === field.key,
    );
    if (entry?.value) {
      contents.push({
        label: field.label,
        format: "text",
        content: entry.value,
      });
    }
  }

  contents.push({
    label: "利用料金",
    format: "currency",
    content: reservation.chargedFeeAmount,
    unit: formatUnitsLabel(reservation.units, unitType) ?? undefined,
  });

  return contents;
}

// ============================================================================
// Format Functions
// ============================================================================

export function formatUnitsLabel(
  units: number | null,
  unitType: FacilityUnitType,
): string | null {
  if (units === null) return null;
  switch (unitType) {
    case "Night":
      return `${units}泊${units + 1}日`;
    case "Reservation":
      return `${units}滞在`;
    case "Slot":
      return `${units}枠`;
    default:
      assertNever(unitType);
  }
}

function getUnitTypeLabel(unitType: FacilityUnitType): string {
  switch (unitType) {
    case "Night":
      return "泊";
    case "Reservation":
      return "滞在";
    case "Slot":
      return "枠";
    default:
      assertNever(unitType);
  }
}

function formatBaseFee(fee: BaseFee, unitType: FacilityUnitType): string {
  const price = (v: number) => formatCurrency(v, { unitCharacter: "円" });
  const unit = getUnitTypeLabel(unitType);
  switch (fee.type) {
    case "free":
      return "無料";
    case "range":
      return `${price(fee.min)}〜${price(fee.max)} / ${unit}`;
    case "uniform": {
      if (fee.tiers.length === 1) {
        return `${price(fee.tiers[0]!.totalFeeAmount)} / ${unit}`;
      }
      return fee.tiers
        .map(
          (t) =>
            `${formatUnitsLabel(t.units, unitType)}: ${price(t.totalFeeAmount)}`,
        )
        .join("\n");
    }
    default:
      assertNever(fee);
  }
}

function formatBookingMethod(params: {
  reserveDeadlineHour: number;
  reserveDeadlineDaysBefore: number;
}): string {
  const { reserveDeadlineHour, reserveDeadlineDaysBefore } = params;

  const hourLabel = `${String(reserveDeadlineHour).padStart(2, "0")}:00`;

  if (reserveDeadlineDaysBefore === 0) {
    return `事前予約制 (当日${hourLabel}まで)`;
  }

  if (reserveDeadlineDaysBefore === 1) {
    return `事前予約制 (前日${hourLabel}まで)`;
  }

  return `事前予約制 (${reserveDeadlineDaysBefore}日前${hourLabel}まで)`;
}
